import { useLayoutEffect, useRef } from "react";
import { useClientTranslations } from "../../lib/i18n/client";
import type { JourneyStep } from "../../types/journey";
import styles from "./StepPills.module.css";

interface StepPillsProps {
	steps: JourneyStep[];
	selectedStepId: string;
	onSelectStep: (stepId: string) => void;
	/** Per-user completed task ids — fallback progress source when no live
	 * step state exists. NOT open/close view state (selecting must not recolor). */
	completedTaskIds?: Set<string>;
	/** Live per-user step progress (UserStepState). When present it is the source
	 * of truth for completed/current — onboarding sets step-level progress before
	 * any task is completed, so task counts alone mislabel the current step. */
	stepStates?: ReadonlyMap<
		string,
		{ status: string; openedAt?: string | null }
	>;
}

export type PillState = "default" | "completed" | "current";

/**
 * Completed when the live step state says "complete"; otherwise fall back to
 * task completion (all tasks done), and for taskless steps to the catalog status.
 */
function isStepCompleted(
	step: JourneyStep,
	completed: Set<string>,
	liveStatus?: string,
): boolean {
	if (liveStatus) return liveStatus === "complete";
	if (step.tasks.length === 0) return step.status === "completed";
	return step.tasks.every((task) => completed.has(task.id));
}

/**
 * Pill state per step. Prefers live step progress: the "current" pill is the
 * live in-progress step; otherwise the first not-yet-completed step (legacy
 * task-based behavior when no step state has been recorded).
 */
export function getPillStates(
	steps: JourneyStep[],
	completedTaskIds?: Set<string>,
	stepStates?: ReadonlyMap<
		string,
		{ status: string; openedAt?: string | null }
	>,
): Map<string, PillState> {
	const completed = completedTaskIds ?? new Set<string>();
	const liveStatusOf = (id: string) => stepStates?.get(id)?.status;
	// Current = the MOST RECENTLY opened in-progress step (max openedAt), matching
	// Journey.tsx + the chat journey-context derivation. Picking the first in
	// catalog order would highlight an earlier pill when multiple steps are open.
	const liveCurrentId = steps
		.filter((s) => liveStatusOf(s.id) === "in_progress")
		.sort((a, b) =>
			(stepStates?.get(b.id)?.openedAt ?? "").localeCompare(
				stepStates?.get(a.id)?.openedAt ?? "",
			),
		)[0]?.id;
	const currentStepId =
		liveCurrentId ??
		steps.find(
			(step) => !isStepCompleted(step, completed, liveStatusOf(step.id)),
		)?.id ??
		null;
	const out = new Map<string, PillState>();
	for (const step of steps) {
		if (isStepCompleted(step, completed, liveStatusOf(step.id))) {
			out.set(step.id, "completed");
		} else if (step.id === currentStepId) {
			out.set(step.id, "current");
		} else {
			out.set(step.id, "default");
		}
	}
	return out;
}

export function StepPills({
	steps,
	selectedStepId,
	onSelectStep,
	completedTaskIds,
	stepStates,
}: StepPillsProps) {
	const { t } = useClientTranslations("journey");
	const containerRef = useRef<HTMLDivElement>(null);
	const pillRefs = useRef<Record<string, HTMLButtonElement | null>>({});

	// Pill color = genuine per-user progress (live step state first, task
	// completion as fallback). Independent of which step is selected/opened, so
	// browsing steps never changes their background — only the selection border.
	const pillStates = getPillStates(steps, completedTaskIds, stepStates);
	const getPillState = (step: JourneyStep): PillState =>
		pillStates.get(step.id) ?? "default";

	// Auto-scroll to the selected step on every mount + selection change.
	// Multi-pass to defeat layout race during route transitions:
	//   - useLayoutEffect runs sync after DOM mutations, before paint
	//   - Then RAF (post-paint #1)
	//   - Then RAF inside RAF (post-paint #2, after any reflow)
	// Position bias: target's left edge sits ~100px from container's left edge,
	// leaving a small peek of the previous step.
	useLayoutEffect(() => {
		if (!selectedStepId) return;
		const target = pillRefs.current[selectedStepId];
		if (!target?.parentElement) return;
		const parent = target.parentElement;
		const LEFT_PEEK_PX = 60;

		const apply = () => {
			const t2 = pillRefs.current[selectedStepId];
			if (!t2) return;
			const desired = Math.max(0, t2.offsetLeft - LEFT_PEEK_PX);
			parent.scrollLeft = desired;
		};

		apply();
		let raf2 = 0;
		const raf1 = requestAnimationFrame(() => {
			apply();
			raf2 = requestAnimationFrame(apply);
		});
		return () => {
			cancelAnimationFrame(raf1);
			if (raf2) cancelAnimationFrame(raf2);
		};
	}, [selectedStepId]);

	return (
		<section className={styles.section}>
			<div className={styles.heading}>
				<h2 className={styles.headingTitle}>{t("steps.heading")}</h2>
				<p className={styles.headingIntro}>{t("steps.intro")}</p>
			</div>
			<div
				ref={containerRef}
				className={styles.pillRow}
				role="tablist"
				aria-label={t("steps.heading")}
			>
				{steps.map((step) => {
					const state = getPillState(step);
					const isSelected = step.id === selectedStepId;
					const label = t(`stepLabels.${step.id}`, {
						defaultValue: step.title,
					});
					return (
						<button
							key={step.id}
							ref={(el) => {
								pillRefs.current[step.id] = el;
							}}
							type="button"
							role="tab"
							aria-selected={isSelected}
							aria-label={label}
							data-state={state}
							data-selected={isSelected}
							className={styles.pill}
							onClick={() => onSelectStep(step.id)}
						>
							{label}
						</button>
					);
				})}
			</div>
		</section>
	);
}
